import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ToasterService } from '../toaster.service';
import { UserService } from '../user.service';
import { SharedDataService } from '../../../modules/shared/shared.data.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NotificationNavigationService {
  private apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}`;

  constructor(
    private router: Router,
    private http: HttpClient,
    private toaster: ToasterService,
    private userService: UserService,
    private sharedDataService: SharedDataService
  ) {}

  /**
   * Handle notification click and navigate to appropriate route
   * @param notification The notification object containing metadata
   */
  async handleNotificationClick(notification: any): Promise<void> {
    let route = '';
    let navigationExtras: any = {};

    try {
      console.log('🔔 [NotificationNavigationService] Notification clicked:', notification);
      console.log('📊 [NotificationNavigationService] Notification structure:', {
        has_meta_data: !!notification.meta_data,
        meta_data_type: typeof notification.meta_data,
        meta_data_value: notification.meta_data,
        has_data: !!notification.data,
        notification_action: notification.notification_action,
        module_id: notification.module_id,
        reference_id: notification.reference_id
      });

      // Extract metadata
      let metaData = notification.meta_data || notification.data?.meta_data;

      console.log('🔍 [NotificationNavigationService] Initial metaData extraction:', {
        metaData: metaData,
        metaData_type: typeof metaData,
        is_string: typeof metaData === 'string',
        is_object: typeof metaData === 'object'
      });

      // Parse meta_data if it's a JSON string
      if (typeof metaData === 'string') {
        try {
          metaData = JSON.parse(metaData);
          console.log('✅ [NotificationNavigationService] Parsed metadata from string:', metaData);
        } catch (e) {
          console.error('❌ [NotificationNavigationService] Failed to parse meta_data:', e);
        }
      }

      console.log('📦 [NotificationNavigationService] Final extracted metadata:', metaData);

      if (!metaData) {
        console.warn('❌ [NotificationNavigationService] Notification has no metadata:', notification);
        this.toaster.showWarningMessage('Notification has no navigation data');
        return;
      }

      // Get route information - support both formats
      route = metaData.route;

      console.log('🗺️ [NotificationNavigationService] Route extraction:', {
        has_direct_route: !!route,
        direct_route: route,
        has_module_id: !!metaData.module_id,
        module_id: metaData.module_id,
        has_data_id: !!metaData.data_id,
        data_id: metaData.data_id,
        has_parent_module_id: !!metaData.parent_module_id,
        parent_module_id: metaData.parent_module_id,
        parent_data_id: metaData.parent_data_id,
        related_module_lower: metaData.related_module_lower
      });

      // If no route provided, build it from module_id and data_id
      if (!route && metaData.module_id && metaData.data_id) {
        console.log('🔍 Building route from meta_data:', {
          module_id: metaData.module_id,
          data_id: metaData.data_id,
          parent_module_id: metaData.parent_module_id,
          parent_data_id: metaData.parent_data_id,
          related_module_lower: metaData.related_module_lower
        });

        // Determine if this is a true child entity (note, task, ticket) or a primary entity (job, proposal)
        // Child entities should navigate to their parent
        // Primary entities should navigate to themselves, even if they have a parent reference
        const childEntityModules = ['note', 'task', 'ticket'];
        const isChildEntity = childEntityModules.includes(metaData.related_module_lower);

        // Use parent routing only for true child entities
        const targetModuleId = (isChildEntity && metaData.parent_module_id)
          ? metaData.parent_module_id
          : metaData.module_id;
        const targetDataId = (isChildEntity && metaData.parent_data_id)
          ? metaData.parent_data_id
          : metaData.data_id;


        route = this.buildRouteFromModuleData(
          targetModuleId,
          targetDataId,
          metaData.related_module_lower
        );
        console.log('🛠️ Built route:', route);
      }

      const deepLink = metaData.deep_link || {};

      console.log('🗺️ [NotificationNavigationService] Final route:', route);
      console.log('🔗 [NotificationNavigationService] Deep link:', deepLink);

      if (!route) {
        console.error('❌ [NotificationNavigationService] No route could be built!', {
          metaData: metaData,
          notification: notification
        });
        this.toaster.showWarningMessage('Unable to navigate - no route specified');
        return;
      }

      // Build query params from deep_link
      const queryParams: any = {};

      // Special handling for Note notifications - add tab index and noteId params
      if (metaData.module_id === 23 && metaData.data_id) {
        queryParams.tab = 12; // Notes tab index in Job detail page
        queryParams.noteId = metaData.data_id;
        console.log('📝 [NotificationNavigationService] Added note-specific params:', {
          tab: 12,
          noteId: metaData.data_id
        });
      }

      if (deepLink.tab) {
        queryParams.tab = deepLink.tab;
      }

      if (deepLink.highlight_id) {
        queryParams.highlight = deepLink.highlight_id;
      }

      if (deepLink.scroll_to) {
        queryParams.scrollTo = deepLink.scroll_to;
      }

      // For note notifications, add tab and noteId query params
      if (metaData.related_module_lower === 'note' && metaData.parent_module_id) {
        // Different parent modules have notes tab at different indices
        const notesTabIndexMap: { [key: number]: number } = {
          13: 5,  // Customer detail - Notes tab at index 5
          16: 8,  // Bid detail - Notes tab at index 8
          19: 12, // Job detail - Notes tab at index 12
          17: 8,  // Proposal detail - Notes tab at index 8
          20: 8,  // Change Order - Notes tab at index 8
          // Add more as needed
        };

        const tabIndex = notesTabIndexMap[metaData.parent_module_id] || 8; // Default to 8
        queryParams.tab = tabIndex;
        queryParams.noteId = metaData.id; // Note ID to open

        console.log('📝 Note notification: Added query params', {
          parent_module_id: metaData.parent_module_id,
          tab: tabIndex,
          noteId: metaData.id
        });
      }

      // For task notifications, add tab and taskId query params
      if (metaData.related_module_lower === 'task' && metaData.parent_module_id) {
        // Different parent modules have tasks tab at different indices
        const tasksTabIndexMap: { [key: number]: number } = {
          16: 6,  // Bid detail - Tasks tab at index 6
          19: 10, // Job detail - Tasks tab at index 10
          17: 6,  // Proposal detail - Tasks tab at index 6
          20: 6,  // Change Order - Tasks tab at index 6
          // Add more as needed
        };

        const tabIndex = tasksTabIndexMap[metaData.parent_module_id] || 6; // Default to 6
        queryParams.tab = tabIndex;
        queryParams.taskId = metaData.id; // Task ID to open

        console.log('✅ [NotificationNavigationService] Task notification detected - adding query params', {
          parent_module_id: metaData.parent_module_id,
          tab: tabIndex,
          taskId: metaData.id,
          metaData: metaData
        });
      } else if (metaData.related_module_lower === 'task') {
        console.log('⚠️ [NotificationNavigationService] Task notification WITHOUT parent_module_id', {
          metaData: metaData,
          has_parent_module_id: !!metaData.parent_module_id,
          related_module_lower: metaData.related_module_lower
        });
      }

      // For ticket notifications, add tab and ticketId query params
      if (metaData.related_module_lower === 'ticket' && metaData.parent_module_id) {
        // Different parent modules have tickets tab at different indices
        const ticketsTabIndexMap: { [key: number]: number } = {
          16: 7,  // Bid detail - Tickets tab at index 7
          19: 11, // Job detail - Tickets tab at index 11
          17: 7,  // Proposal detail - Tickets tab at index 7
          20: 7,  // Change Order - Tickets tab at index 7
          // Add more as needed
        };

        const tabIndex = ticketsTabIndexMap[metaData.parent_module_id] || 7; // Default to 7
        queryParams.tab = tabIndex;
        queryParams.ticketId = metaData.id; // Ticket ID to open

        console.log('🎫 [NotificationNavigationService] Ticket notification detected - adding query params', {
          parent_module_id: metaData.parent_module_id,
          tab: tabIndex,
          ticketId: metaData.id,
          metaData: metaData
        });
      } else if (metaData.related_module_lower === 'ticket') {
        console.log('⚠️ [NotificationNavigationService] Ticket notification WITHOUT parent_module_id', {
          metaData: metaData,
          has_parent_module_id: !!metaData.parent_module_id,
          related_module_lower: metaData.related_module_lower
        });
      }

      // Additional deep link parameters
      Object.keys(deepLink).forEach((key) => {
        if (!['tab', 'highlight_id', 'scroll_to'].includes(key)) {
          queryParams[key] = deepLink[key];
        }
      });

      // Special routing for Accounting users on "Proposal Converted to Job" notifications
      // Route them to the Documents tab (index 14) instead of the default tab
      if (notification.notification_action === 'proposal_converted' && metaData.module_id === 19) {
        const userRoles = this.sharedDataService.getCurrentUserRoles();

        // Check if user has Accounting role (Role ID: 8 or Role Name: 'Accounting')
        const isAccountingUser = userRoles?.some((role: any) =>
          role.id === 8 || role.id === '8' || role.name === 'Accounting'
        );

        if (isAccountingUser) {
          queryParams.tab = 14; // Documents tab index for Job detail page
        }
      }

      // Navigate to the route
      navigationExtras = Object.keys(queryParams).length > 0
        ? { queryParams }
        : {};

      console.log('🚀 [NotificationNavigationService] Navigating to:', {
        route: route,
        navigationExtras: navigationExtras,
        queryParams: queryParams,
        full_metadata: metaData,
        notification_action: notification.notification_action
      });

      const result = await this.router.navigate([route], navigationExtras);

      if (result) {
        console.log('✅ [NotificationNavigationService] Navigation successful!');
      } else {
        console.warn('⚠️ [NotificationNavigationService] Navigation returned false - route might not exist:', {
          route: route,
          navigationExtras: navigationExtras
        });
      }

    } catch (error) {
      console.error('❌ Navigation error:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        route: route,
        navigationExtras: navigationExtras
      });
      this.toaster.showErrorMessage('Failed to navigate to notification target');
    }
  }

  /**
   * Mark notification as read
   * @param notificationId The notification ID
   */
  markAsRead(notificationId: number): void {
    const url = `${this.apiUrl}notifications/seen/${notificationId}`;
    this.http
      .post(url, {})
      .subscribe({
        next: () => {
          console.log(`✅ Notification ${notificationId} marked as read`);
        },
        error: (error) => {
          console.error('Failed to mark notification as read:', error);
        },
      });
  }

  /**
   * Process notification click - mark as read and navigate
   * @param notification The full notification object
   */
  async processNotificationClick(notification: any): Promise<void> {
    // Mark as read
    if (notification.id || notification.notification_id) {
      const notifId = notification.id || notification.notification_id;
      this.markAsRead(notifId);
    }

    // Navigate
    await this.handleNotificationClick(notification);
  }

  /**
   * Handle push notification click (from Firebase)
   * @param payload The FCM payload
   */
  async handlePushNotificationClick(payload: any): Promise<void> {
    console.log('Handling push notification click:', payload);

    // FCM payload structure can vary
    const notificationData = payload.data || payload.notification || payload;

    // Extract meta_data (might be JSON string in FCM)
    let metaData = notificationData.meta_data;
    if (typeof metaData === 'string') {
      try {
        metaData = JSON.parse(metaData);
      } catch (e) {
        console.error('Failed to parse meta_data:', e);
        return;
      }
    }

    // Construct notification object
    const notification = {
      id: notificationData.notification_id || null,
      meta_data: metaData,
      data: notificationData,
    };

    await this.processNotificationClick(notification);
  }

  /**
   * Build route from module_id and data_id
   * @param moduleId Module ID from backend
   * @param dataId Record ID
   * @param moduleLower Module name in lowercase (optional)
   * @returns Route path
   */
  private buildRouteFromModuleData(
    moduleId: number,
    dataId: number,
    moduleLower?: string
  ): string {

    // Map module IDs to full route patterns
    // Each module has its own routing pattern based on Angular routing configuration
    const moduleRouteMap: { [key: number]: string } = {
      1: `/customers/customer/${dataId}`,      // Customer detail
      3: `/proposals/detail/${dataId}`,        // Proposal detail (legacy)
      7: `/tasks`,                             // Tasks list (opens side panel via taskId query param)
      8: `/tickets`,                           // Tickets list (opens side panel via ticketId query param)
      13: `/customers/customer/${dataId}`,     // Customer detail
      16: `/bids/bid/${dataId}`,               // Bid detail
      17: `/proposals/detail/${dataId}`,       // Proposal detail (standard)
      19: `/jobs/detail/${dataId}`,            // Job detail
      20: `/change-orders/view/${dataId}`,     // Change order view
      21: `/purchase-order/detail/${dataId}`,  // Purchase order detail (note: singular)
      22: `/submittals/view/${dataId}`,        // Submittal view
      23: `/bids/bid/${dataId}`,               // Note module - navigate to parent bid (TEMPORARY - should use parent_data_id)
      // Add more module mappings as needed
    };

    const route = moduleRouteMap[moduleId];

    if (!route) {
      console.error('❌ Unknown module_id:', moduleId);
      return '';
    }

    return route;
  }

  /**
   * Validate if user has permission to view the resource
   * @param module The module name
   * @returns boolean indicating if user can navigate
   */
  private async validatePermission(module: string): Promise<boolean> {
    // TODO: Implement permission check based on your permission system
    // For now, return true. You can integrate with your UserService
    return true;
  }

  /**
   * Check if resource exists before navigating
   * @param route The route to check
   * @returns boolean indicating if resource exists
   */
  private async checkResourceExists(route: string): Promise<boolean> {
    // TODO: Optionally implement a check to see if resource still exists
    // This can prevent navigating to deleted resources
    return true;
  }
}
